import type { CreateCustomer, Customer } from '@monorepo/shared'
import type {
  CurrencyCode,
  CustomerStatus,
  CustomerType,
  ProfileKind,
} from '@monorepo/shared/schemas/common'
import {
  CUSTOMER_STATUS,
  CUSTOMER_STATUSES,
  CUSTOMER_TYPE,
  CUSTOMER_TYPES,
  PROFILE_KIND,
  PROFILE_KINDS,
} from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { Building2, Loader2, Percent, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TaxAssignmentDialog } from '@/shared/components/taxes'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import { toast } from '@/shared/lib/toast'
import { useCreateCustomer, useUpdateCustomer } from '../hooks/useCustomers'
import { useOrganizations } from '../hooks/useOrganizations'
import { organizationsService } from '../services/organizationsService'
import { OrganizationDrawer } from './OrganizationDrawer'

interface CustomerDrawerProps {
  customer?: Customer // undefined = CREATE mode, defined = EDIT mode
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Form type with required defaults
interface CustomerFormData {
  type: CustomerType
  profile_kind: ProfileKind | ''
  name: string
  email: string
  phone: string
  external_id: string
  agent_name: string
  organization_id: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  postal_code: string
  country: string
  currency: CurrencyCode | ''
  timezone: string
  tax_exempt: boolean
  tax_exemption_certificate: string
  tax_exemption_reason: string
  status: CustomerStatus
  metadata: Record<string, unknown>
}

export function CustomerDrawer({
  customer,
  open,
  onOpenChange,
}: CustomerDrawerProps) {
  const navigate = useNavigate()
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false)
  const [taxDialogOpen, setTaxDialogOpen] = useState(false)

  const createCustomerMutation = useCreateCustomer()
  const updateCustomerMutation = useUpdateCustomer()

  const { data: organizationsData, isLoading: isLoadingOrganizations } =
    useOrganizations()
  const organizations = organizationsData?.data || []

  const form = useForm<CustomerFormData>({
    defaultValues: {
      type: CUSTOMER_TYPE.individual,
      profile_kind: PROFILE_KIND.human,
      name: '',
      email: '',
      phone: '',
      external_id: '',
      agent_name: '',
      organization_id: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      currency: 'USD',
      timezone: 'UTC',
      tax_exempt: false,
      tax_exemption_certificate: '',
      tax_exemption_reason: '',
      status: CUSTOMER_STATUS.active,
      metadata: {},
    },
  })

  // Update form data when customer changes or reset when creating
  useEffect(() => {
    const loadCustomerData = async () => {
      if (customer && open) {
        // Edit mode: populate with customer data

        // If customer has organization but no address, fetch organization address
        let orgAddress = {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: '',
        }

        if (customer.organization_id && !customer.address_line1) {
          try {
            const org = await organizationsService.getOrganization(
              customer.organization_id
            )
            if (org.address_line1) {
              orgAddress = {
                address_line1: org.address_line1 || '',
                address_line2: org.address_line2 || '',
                city: org.city || '',
                state: org.state || '',
                postal_code: org.postal_code || '',
                country: org.country || '',
              }
            }
          } catch {
            // Organization address failed to load
          }
        }

        form.reset({
          type: customer.type,
          profile_kind: customer.profile_kind || '',
          name: customer.name,
          email: customer.email || '',
          phone: customer.phone || '',
          external_id: customer.external_id || '',
          agent_name: customer.agent_name || '',
          organization_id: customer.organization_id || '',
          // Use customer address or fallback to organization address
          address_line1: customer.address_line1 || orgAddress.address_line1,
          address_line2: customer.address_line2 || orgAddress.address_line2,
          city: customer.city || orgAddress.city,
          state: customer.state || orgAddress.state,
          postal_code: customer.postal_code || orgAddress.postal_code,
          country: customer.country || orgAddress.country,
          currency: (customer.currency as CurrencyCode) || 'USD',
          timezone: customer.timezone || 'UTC',
          tax_exempt: customer.tax_exempt || false,
          tax_exemption_certificate: '',
          tax_exemption_reason: '',
          status: customer.status,
          metadata: customer.metadata || {},
        })
      } else if (!customer && open) {
        // Create mode: reset to default values
        form.reset({
          type: CUSTOMER_TYPE.individual,
          profile_kind: PROFILE_KIND.human,
          name: '',
          email: '',
          phone: '',
          external_id: '',
          agent_name: '',
          organization_id: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: '',
          currency: 'USD',
          timezone: 'UTC',
          tax_exempt: false,
          tax_exemption_certificate: '',
          tax_exemption_reason: '',
          status: CUSTOMER_STATUS.active,
          metadata: {},
        })
      }
    }

    loadCustomerData()
  }, [customer, open, form])

  const onSubmit = async (data: CustomerFormData) => {
    try {
      // Validate company type requires organization
      if (data.type === CUSTOMER_TYPE.company && !data.organization_id) {
        toast.error('Organization is required for company customers')
        return
      }

      // Validate AI agent profile requires agent_name
      if (data.profile_kind === PROFILE_KIND.ai_agent && !data.agent_name) {
        toast.error('Agent name is required for AI agent profiles')
        return
      }

      // Convert form data to CreateCustomer format
      const customerData: CreateCustomer = {
        type: data.type,
        profile_kind: data.profile_kind || undefined,
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        external_id: data.external_id || undefined,
        agent_name: data.agent_name || undefined,
        organization_id: data.organization_id || undefined,
        address_line1: data.address_line1 || undefined,
        address_line2: data.address_line2 || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        postal_code: data.postal_code || undefined,
        country: data.country || undefined,
        currency: data.currency || undefined,
        timezone: data.timezone || undefined,
        tax_exempt: data.tax_exempt,
        tax_exemption_certificate: data.tax_exemption_certificate || undefined,
        tax_exemption_reason: data.tax_exemption_reason || undefined,
        status: data.status,
        metadata: data.metadata,
      }

      if (customer) {
        // Edit mode: update existing customer
        await updateCustomerMutation.mutateAsync({
          id: customer.id,
          data: customerData,
        })
        onOpenChange(false)
      } else {
        // Create mode: create new customer
        const newCustomer =
          await createCustomerMutation.mutateAsync(customerData)
        onOpenChange(false)
        navigate({
          to: '/customers/$customerId',
          params: { customerId: newCustomer.id },
        })
      }
    } catch {
      // Error handled by mutation hooks
    }
  }

  const handleCancel = () => {
    if (customer) {
      // Reset form to original values
      form.reset()
    }
    onOpenChange(false)
  }

  const isEditMode = !!customer
  const isSaving =
    createCustomerMutation.isPending || updateCustomerMutation.isPending

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
        <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
          <DrawerHeader>
            <DrawerTitle>
              {isEditMode ? 'Edit Customer' : 'Create Customer'}
            </DrawerTitle>
            <DrawerDescription>
              {isEditMode
                ? 'Update customer information and settings'
                : 'Add a new customer to your system'}
            </DrawerDescription>
          </DrawerHeader>

          <div className='flex-1 overflow-y-auto px-4 pb-6'>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              {/* Basic Information */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Basic Information</h3>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='type'>Customer Type</Label>
                    <Select
                      value={form.watch('type')}
                      onValueChange={value =>
                        form.setValue('type', value as CustomerType)
                      }
                    >
                      <SelectTrigger id='type'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CUSTOMER_TYPES.map(type => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='profile_kind'>Profile Kind</Label>
                    <Select
                      value={form.watch('profile_kind') || undefined}
                      onValueChange={value =>
                        form.setValue('profile_kind', value as ProfileKind)
                      }
                    >
                      <SelectTrigger id='profile_kind'>
                        <SelectValue placeholder='Select profile kind (optional)' />
                      </SelectTrigger>
                      <SelectContent>
                        {PROFILE_KINDS.map(kind => (
                          <SelectItem key={kind} value={kind}>
                            {kind.replace('_', ' ').charAt(0).toUpperCase() +
                              kind.slice(1).replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='name'>Name *</Label>
                  <Input
                    id='name'
                    {...form.register('name', { required: true })}
                    placeholder='Customer name'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='email'>Email</Label>
                    <Input
                      id='email'
                      type='email'
                      {...form.register('email')}
                      placeholder='email@example.com'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='phone'>Phone</Label>
                    <Input
                      id='phone'
                      {...form.register('phone')}
                      placeholder='+1234567890'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='external_id'>External ID</Label>
                  <Input
                    id='external_id'
                    {...form.register('external_id')}
                    placeholder='External system identifier (e.g., Stripe customer ID)'
                  />
                  <p className='text-xs text-muted-foreground'>
                    Optional identifier for linking to external systems
                  </p>
                </div>

                {form.watch('profile_kind') === PROFILE_KIND.ai_agent && (
                  <div className='space-y-2'>
                    <Label htmlFor='agent_name'>Agent Name *</Label>
                    <Input
                      id='agent_name'
                      {...form.register('agent_name')}
                      placeholder='Agent identifier (required for AI agents)'
                    />
                  </div>
                )}
              </div>

              {/* Organization */}
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-sm font-medium'>Organization</h3>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setOrgDrawerOpen(true)}
                  >
                    <Building2 className='mr-2 h-4 w-4' />
                    New Organization
                  </Button>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='organization_id'>
                    Organization
                    {form.watch('type') === CUSTOMER_TYPE.company && ' *'}
                  </Label>
                  <Select
                    value={form.watch('organization_id') || undefined}
                    onValueChange={value =>
                      form.setValue('organization_id', value)
                    }
                  >
                    <SelectTrigger id='organization_id'>
                      <SelectValue
                        placeholder={
                          form.watch('type') === CUSTOMER_TYPE.company
                            ? 'Select organization (required)'
                            : 'Select organization (optional)'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingOrganizations ? (
                        <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                          Loading organizations...
                        </div>
                      ) : organizations.length === 0 ? (
                        <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                          No organizations available
                        </div>
                      ) : (
                        organizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Address</h3>

                <div className='space-y-2'>
                  <Label htmlFor='address_line1'>Address Line 1</Label>
                  <Input
                    id='address_line1'
                    {...form.register('address_line1')}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='address_line2'>Address Line 2</Label>
                  <Input
                    id='address_line2'
                    {...form.register('address_line2')}
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>City</Label>
                    <Input id='city' {...form.register('city')} />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='state'>State/Province</Label>
                    <Input id='state' {...form.register('state')} />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='postal_code'>Postal Code</Label>
                    <Input id='postal_code' {...form.register('postal_code')} />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='country'>Country</Label>
                    <Input
                      id='country'
                      {...form.register('country')}
                      placeholder='US'
                    />
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Preferences</h3>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='currency'>Currency</Label>
                    <Input
                      id='currency'
                      {...form.register('currency')}
                      placeholder='USD'
                      maxLength={3}
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='timezone'>Timezone</Label>
                    <Input
                      id='timezone'
                      {...form.register('timezone')}
                      placeholder='UTC'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='status'>Status</Label>
                  <Select
                    value={form.watch('status')}
                    onValueChange={value =>
                      form.setValue('status', value as CustomerStatus)
                    }
                  >
                    <SelectTrigger id='status'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tax Settings */}
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-sm font-medium'>Tax Settings</h3>
                  {isEditMode && !form.watch('tax_exempt') && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setTaxDialogOpen(true)}
                    >
                      <Percent className='mr-2 h-4 w-4' />
                      Manage Taxes
                    </Button>
                  )}
                </div>

                <div className='flex items-center justify-between rounded-lg border p-4'>
                  <div className='space-y-0.5'>
                    <Label htmlFor='tax_exempt'>Tax Exempt</Label>
                    <p className='text-sm text-muted-foreground'>
                      Customer is exempt from all taxes
                    </p>
                  </div>
                  <Switch
                    id='tax_exempt'
                    checked={form.watch('tax_exempt')}
                    onCheckedChange={checked =>
                      form.setValue('tax_exempt', checked)
                    }
                  />
                </div>

                {form.watch('tax_exempt') && (
                  <>
                    <div className='space-y-2'>
                      <Label htmlFor='tax_exemption_certificate'>
                        Exemption Certificate
                      </Label>
                      <Input
                        id='tax_exemption_certificate'
                        {...form.register('tax_exemption_certificate')}
                        placeholder='Certificate number or ID'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='tax_exemption_reason'>
                        Exemption Reason
                      </Label>
                      <Input
                        id='tax_exemption_reason'
                        {...form.register('tax_exemption_reason')}
                        placeholder='Reason for tax exemption'
                      />
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>

          <DrawerFooter>
            <div className='flex gap-2 w-full'>
              <DrawerClose asChild>
                <Button
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isSaving}
                  className='flex-1'
                >
                  <X className='w-4 h-4 mr-2' />
                  Cancel
                </Button>
              </DrawerClose>
              <Button
                onClick={form.handleSubmit(onSubmit)}
                disabled={isSaving}
                className='flex-1'
              >
                {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                <Save className='w-4 h-4 mr-2' />
                {isEditMode ? 'Update Customer' : 'Create Customer'}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Organization Drawer (nested) */}
      <OrganizationDrawer
        open={orgDrawerOpen}
        onOpenChange={setOrgDrawerOpen}
        onOrganizationCreated={org => {
          form.setValue('organization_id', org.id)
          setOrgDrawerOpen(false)
        }}
      />

      {/* Tax Assignment Dialog */}
      {isEditMode && customer && (
        <TaxAssignmentDialog
          open={taxDialogOpen}
          onOpenChange={setTaxDialogOpen}
          entityType='customer'
          entityName={customer.name}
          currentTaxIds={[]} // TODO: Get from customer taxes
          onAssign={async _data => {
            // TODO: Call assignTaxes service method
          }}
        />
      )}
    </>
  )
}
